import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft, Play, Files, ListChecks, Receipt,
  Scales, CurrencyInr, Spinner, Plus, Timer, ShieldCheck, Detective
} from "@phosphor-icons/react";
import PdfViewer from "@/components/PdfViewer";
import DuplicateAlert from "@/components/DuplicateAlert";
import FraudAlert from "@/components/FraudAlert";
import DocumentIntegrityTab from "@/components/DocumentIntegrityTab";
import FraudEngineTab from "@/components/FraudEngineTab";
import UploadDialog from "@/components/UploadDialog";
import OverviewTab from "@/components/OverviewTab";
import ExtractedDataTab from "@/components/ExtractedDataTab";
import BillsTab from "@/components/BillsTab";
import RuleEngineTab from "@/components/RuleEngineTab";
import FinalAmountTab from "@/components/FinalAmountTab";
import useFraudAnalysis from "@/hooks/useFraudAnalysis";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClaimDetail() {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "overview");
  const [selectedPage, setSelectedPage] = useState(1);
  const [showAddFiles, setShowAddFiles] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const fraud = useFraudAnalysis(claimId, claim?.status, claim?.fraud_summary?.analyzed_at);
  const pollingRef = useRef(null);
  const toastShownRef = useRef(false);

  const fetchClaim = useCallback(async (silent = false) => {
    try {
      const res = await axios.get(`${API}/claims/${claimId}`, { timeout: 30000 });
      setClaim(res.data);
      return res.data;
    } catch (err) {
      if (!silent) {
        // Only show error toast on initial load, not during polling
        const msg = err?.response?.status === 404 ? "Claim not found" :
                    err?.code === "ECONNABORTED" ? "Request timed out — claim may be processing" :
                    "Failed to load claim data";
        toast.error(msg);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [claimId]);

  // Initial load
  useEffect(() => {
    fetchClaim();
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [fetchClaim]);

  // Polling when processing — use silent mode to avoid error toasts
  const claimStatus = claim?.status;
  useEffect(() => {
    if (claimStatus === "processing") {
      if (!pollingRef.current) {
        pollingRef.current = setInterval(async () => {
          const updated = await fetchClaim(true);
          if (updated && updated.status !== "processing") {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setProcessing(false);
            if (updated.status === "processed" && !toastShownRef.current) {
              toastShownRef.current = true;
              toast.success(`Processing complete! Decision: ${updated.final_amount?.decision?.replace(/_/g, " ")}`);
            } else if (updated.status === "error" && !toastShownRef.current) {
              toastShownRef.current = true;
              const lastLog = updated.processing_log?.[updated.processing_log.length - 1];
              toast.error(lastLog?.message?.slice(0, 100) || "Processing failed");
            }
          }
        }, 3000);
      }
    } else {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }
  }, [claimStatus, fetchClaim]);

  const handleProcess = async () => {
    setProcessing(true);
    toastShownRef.current = false;
    try {
      toast.info("Processing started. AI is analyzing your document...");
      await axios.post(`${API}/claims/${claimId}/process`);
      setClaim(prev => ({ ...prev, status: "processing", progress: 0 }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Processing failed");
      setProcessing(false);
      fetchClaim();
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex" data-testid="claim-detail-loading">
        <div className="w-[45%] border-r border-gray-200 bg-gray-100 p-4">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-[80vh] w-full" />
        </div>
        <div className="w-[55%] p-6">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-10 w-full mb-4" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!claim) {
    return (
      <div className="h-screen flex items-center justify-center">
        <p className="text-gray-500">Claim not found</p>
      </div>
    );
  }

  const isProcessed = claim.status === "processed";
  const isProcessing = claim.status === "processing";
  const isError = claim.status === "error";
  const canProcess = claim.status === "uploaded" || claim.status === "error";
  const hasDuplicates = (claim.duplicates?.length || 0) > 0;
  const progress = claim.progress || 0;

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50" data-testid="claim-detail-page">
      {/* Left: PDF Viewer */}
      <div className="w-[45%] h-full border-r border-gray-200 bg-[#E5E7EB] flex flex-col flex-shrink-0">
        {/* Left Header */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              data-testid="back-btn"
              className="gap-1 text-gray-600"
            >
              <ArrowLeft size={16} /> Back
            </Button>
            <div className="h-5 w-px bg-gray-200" />
            <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]" data-testid="file-name">
              {claim.file_name}
            </span>
            <Badge variant="outline" className="text-xs">
              {claim.total_pages} pages
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            {!isProcessing && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddFiles(true)}
                className="gap-1.5 text-sm"
                data-testid="add-files-btn"
              >
                <Plus size={14} /> Add Files
              </Button>
            )}
            {canProcess && (
              <Button
                onClick={handleProcess}
                disabled={processing}
                className="bg-[#002FA7] hover:bg-blue-800 text-white gap-2 text-sm"
                data-testid="process-btn"
              >
                {processing ? (
                  <>
                    <Spinner size={16} className="animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Play size={16} weight="fill" />
                    {isError ? "Retry Processing" : "Process with AI"}
                  </>
                )}
              </Button>
            )}
            {isProcessing && (
              <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300 gap-1.5" variant="outline">
                <Spinner size={12} className="animate-spin" />
                Processing...
              </Badge>
            )}
            {isProcessed && (
              <>
                <Badge className="bg-green-100 text-green-800 border-green-300" variant="outline">
                  Processed
                </Badge>
                {claim.tat && (
                  <Badge className="bg-gray-100 text-gray-700 border-gray-300 gap-1" variant="outline" data-testid="tat-badge">
                    <Timer size={12} />
                    {claim.tat.total_display} ({claim.tat.seconds_per_page}s/page)
                  </Badge>
                )}
              </>
            )}
          </div>
        </div>

        {/* Processing progress bar */}
        {isProcessing && (
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">AI analyzing document...</span>
              <span className="text-xs font-medium text-[#002FA7]">{progress}%</span>
            </div>
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-gray-400 mt-1">
              {progress < 50 ? "Classifying pages..." :
               progress < 90 ? "Extracting data from documents..." :
               "Applying rules and calculating amounts..."}
            </p>
          </div>
        )}

        {/* Error banner */}
        {isError && (
          <div className="flex-shrink-0 bg-red-50 border-b border-red-200 px-4 py-2">
            <p className="text-xs text-red-700">
              Processing failed. {claim.processing_log?.[claim.processing_log.length - 1]?.message?.includes("budget")
                ? "Please add balance to your Universal Key and retry."
                : "Click 'Retry Processing' to try again."}
            </p>
          </div>
        )}

        {/* PDF Pages */}
        <PdfViewer
          claimId={claimId}
          totalPages={claim.total_pages}
          pages={claim.pages || []}
          selectedPage={selectedPage}
          onSelectPage={setSelectedPage}
          isProcessed={isProcessed}
          fraud={fraud}
          showHeatmap={showHeatmap}
          onToggleHeatmap={() => setShowHeatmap(v => !v)}
        />
      </div>

      {/* Right: Data Panels */}
      <div className="w-[55%] h-full bg-white flex flex-col overflow-hidden">
        {/* Duplicate Alert */}
        {hasDuplicates && (
          <DuplicateAlert duplicates={claim.duplicates} />
        )}

        {/* Fraud Alert */}
        <FraudAlert claim={claim} />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-shrink-0 border-b border-gray-200 px-4">
            <TabsList className="bg-transparent h-auto p-0 gap-0" data-testid="detail-tabs">
              {[
                { value: "overview", label: "Overview", icon: Files },
                { value: "extracted", label: "Extracted Data", icon: ListChecks },
                { value: "bills", label: "Bills & Line Items", icon: Receipt },
                { value: "rules", label: "Rule Engine", icon: Scales },
                { value: "final", label: "Final Amount", icon: CurrencyInr },
                { value: "integrity", label: "Doc Integrity", icon: ShieldCheck },
                { value: "fraud", label: "Fraud Engine", icon: Detective }
              ].map(tab => {
                const Icon = tab.icon;
                return (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    data-testid={`tab-${tab.value}`}
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-[#002FA7] px-4 py-2.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors gap-1.5"
                  >
                    <Icon size={16} />
                    {tab.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          <div className="flex-1 overflow-auto">
            <TabsContent value="overview" className="mt-0 h-full">
              <OverviewTab claim={claim} onSelectPage={setSelectedPage} onUpdate={fetchClaim} />
            </TabsContent>
            <TabsContent value="extracted" className="mt-0 h-full">
              <ExtractedDataTab claim={claim} selectedPage={selectedPage} onSelectPage={setSelectedPage} />
            </TabsContent>
            <TabsContent value="bills" className="mt-0 h-full">
              <BillsTab claim={claim} />
            </TabsContent>
            <TabsContent value="rules" className="mt-0 h-full">
              <RuleEngineTab claim={claim} onReprocess={fetchClaim} />
            </TabsContent>
            <TabsContent value="final" className="mt-0 h-full">
              <FinalAmountTab claim={claim} />
            </TabsContent>
            <TabsContent value="integrity" className="mt-0 h-full">
              <DocumentIntegrityTab claim={claim} />
            </TabsContent>
            <TabsContent value="fraud" className="mt-0 h-full">
              <FraudEngineTab claim={claim} fraud={fraud} selectedPage={selectedPage} onSelectPage={setSelectedPage} showHeatmap={showHeatmap} onToggleHeatmap={() => setShowHeatmap(v => !v)} />
            </TabsContent>
          </div>
        </Tabs>
      </div>

      {/* Add Files Dialog */}
      <UploadDialog
        open={showAddFiles}
        onOpenChange={setShowAddFiles}
        existingClaimId={claimId}
        onClaimCreated={() => { setShowAddFiles(false); fetchClaim(); }}
      />
    </div>
  );
}
